import { z } from 'zod';
import { Prisma } from '@prisma/garther-client';

/////////////////////////////////////////
// HELPER FUNCTIONS
/////////////////////////////////////////

// JSON
//------------------------------------------------------

export type NullableJsonInput = Prisma.JsonValue | null | 'JsonNull' | 'DbNull' | Prisma.NullTypes.DbNull | Prisma.NullTypes.JsonNull;

export const transformJsonNull = (v?: NullableJsonInput) => {
  if (!v || v === 'DbNull') return Prisma.NullTypes.DbNull;
  if (v === 'JsonNull') return Prisma.NullTypes.JsonNull;
  return v;
};

export const JsonValueSchema: z.ZodType<Prisma.JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.literal(null),
    z.record(z.string(), z.lazy(() => JsonValueSchema.optional())),
    z.array(z.lazy(() => JsonValueSchema)),
  ])
);

export type JsonValueType = z.infer<typeof JsonValueSchema>;

export const NullableJsonValue = z
  .union([JsonValueSchema, z.literal('DbNull'), z.literal('JsonNull')])
  .nullable()
  .transform((v) => transformJsonNull(v));

export type NullableJsonValueType = z.infer<typeof NullableJsonValue>;

export const InputJsonValueSchema: z.ZodType<Prisma.InputJsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.object({ toJSON: z.any() }),
    z.record(z.string(), z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
    z.array(z.lazy(() => z.union([InputJsonValueSchema, z.literal(null)]))),
  ])
);

export type InputJsonValueType = z.infer<typeof InputJsonValueSchema>;


/////////////////////////////////////////
// ENUMS
/////////////////////////////////////////

export enum TransactionIsolationLevel {ReadUncommitted = 'ReadUncommitted',ReadCommitted = 'ReadCommitted',RepeatableRead = 'RepeatableRead',Serializable = 'Serializable',}

export const TransactionIsolationLevelSchema = z.nativeEnum(TransactionIsolationLevel);

export type TransactionIsolationLevelType = z.infer<typeof TransactionIsolationLevelSchema>;

export enum AccountScalarFieldEnum {id = 'id',referralCode = 'referralCode',orgId = 'orgId',userId = 'userId',parentId = 'parentId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const AccountScalarFieldEnumSchema = z.nativeEnum(AccountScalarFieldEnum);

export type AccountScalarFieldEnumType = z.infer<typeof AccountScalarFieldEnumSchema>;

export enum ReferralStatsScalarFieldEnum {accountId = 'accountId',level1Count = 'level1Count',level1Earned = 'level1Earned',level1EarnPending = 'level1EarnPending',level2Count = 'level2Count',level2Earned = 'level2Earned',level2EarnPending = 'level2EarnPending',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const ReferralStatsScalarFieldEnumSchema = z.nativeEnum(ReferralStatsScalarFieldEnum);

export type ReferralStatsScalarFieldEnumType = z.infer<typeof ReferralStatsScalarFieldEnumSchema>;

export enum BookingScalarFieldEnum {id = 'id',userId = 'userId',ticketTierId = 'ticketTierId',quantity = 'quantity',paymentMethod = 'paymentMethod',status = 'status',budget = 'budget',languages = 'languages',dietaryRestrictions = 'dietaryRestrictions',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const BookingScalarFieldEnumSchema = z.nativeEnum(BookingScalarFieldEnum);

export type BookingScalarFieldEnumType = z.infer<typeof BookingScalarFieldEnumSchema>;

export enum CreditScalarFieldEnum {accountId = 'accountId',balance = 'balance',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditScalarFieldEnumSchema = z.nativeEnum(CreditScalarFieldEnum);

export type CreditScalarFieldEnumType = z.infer<typeof CreditScalarFieldEnumSchema>;

export enum CreditTxScalarFieldEnum {id = 'id',creditId = 'creditId',desc = 'desc',amount = 'amount',type = 'type',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditTxScalarFieldEnumSchema = z.nativeEnum(CreditTxScalarFieldEnum);

export type CreditTxScalarFieldEnumType = z.infer<typeof CreditTxScalarFieldEnumSchema>;

export enum CreditTxPurchaseMetaScalarFieldEnum {creditTxId = 'creditTxId',method = 'method',status = 'status',currency = 'currency',txId = 'txId',qrCode = 'qrCode',checkoutUrl = 'checkoutUrl',expiresAt = 'expiresAt',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditTxPurchaseMetaScalarFieldEnumSchema = z.nativeEnum(CreditTxPurchaseMetaScalarFieldEnum);

export type CreditTxPurchaseMetaScalarFieldEnumType = z.infer<typeof CreditTxPurchaseMetaScalarFieldEnumSchema>;

export enum CreditTxReferralRewardMetaScalarFieldEnum {creditTxId = 'creditTxId',referralId = 'referralId',level = 'level',status = 'status',creditTxUsageMetaId = 'creditTxUsageMetaId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditTxReferralRewardMetaScalarFieldEnumSchema = z.nativeEnum(CreditTxReferralRewardMetaScalarFieldEnum);

export type CreditTxReferralRewardMetaScalarFieldEnumType = z.infer<typeof CreditTxReferralRewardMetaScalarFieldEnumSchema>;

export enum CreditTxTicketSoldMetaScalarFieldEnum {creditTxId = 'creditTxId',status = 'status',creditTxUsageMetaId = 'creditTxUsageMetaId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditTxTicketSoldMetaScalarFieldEnumSchema = z.nativeEnum(CreditTxTicketSoldMetaScalarFieldEnum);

export type CreditTxTicketSoldMetaScalarFieldEnumType = z.infer<typeof CreditTxTicketSoldMetaScalarFieldEnumSchema>;

export enum CreditTxUsageMetaScalarFieldEnum {creditTxId = 'creditTxId',bookingId = 'bookingId',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const CreditTxUsageMetaScalarFieldEnumSchema = z.nativeEnum(CreditTxUsageMetaScalarFieldEnum);

export type CreditTxUsageMetaScalarFieldEnumType = z.infer<typeof CreditTxUsageMetaScalarFieldEnumSchema>;

export enum EventScalarFieldEnum {id = 'id',hostId = 'hostId',title = 'title',description = 'description',banner = 'banner',featuredImages = 'featuredImages',date = 'date',status = 'status',locationId = 'locationId',categoryId = 'categoryId',taken = 'taken',cap = 'cap',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const EventScalarFieldEnumSchema = z.nativeEnum(EventScalarFieldEnum);

export type EventScalarFieldEnumType = z.infer<typeof EventScalarFieldEnumSchema>;

export enum EventCategoryScalarFieldEnum {id = 'id',name = 'name',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const EventCategoryScalarFieldEnumSchema = z.nativeEnum(EventCategoryScalarFieldEnum);

export type EventCategoryScalarFieldEnumType = z.infer<typeof EventCategoryScalarFieldEnumSchema>;

export enum TicketScalarFieldEnum {id = 'id',ticketTierId = 'ticketTierId',bookingId = 'bookingId',status = 'status',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const TicketScalarFieldEnumSchema = z.nativeEnum(TicketScalarFieldEnum);

export type TicketScalarFieldEnumType = z.infer<typeof TicketScalarFieldEnumSchema>;

export enum TicketTierScalarFieldEnum {id = 'id',eventId = 'eventId',title = 'title',description = 'description',status = 'status',type = 'type',taken = 'taken',cap = 'cap',price = 'price',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const TicketTierScalarFieldEnumSchema = z.nativeEnum(TicketTierScalarFieldEnum);

export type TicketTierScalarFieldEnumType = z.infer<typeof TicketTierScalarFieldEnumSchema>;

export enum InvitationScalarFieldEnum {id = 'id',bookingId = 'bookingId',inviteeId = 'inviteeId',status = 'status',expiresAt = 'expiresAt',respondedAt = 'respondedAt',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const InvitationScalarFieldEnumSchema = z.nativeEnum(InvitationScalarFieldEnum);

export type InvitationScalarFieldEnumType = z.infer<typeof InvitationScalarFieldEnumSchema>;

export enum LocationScalarFieldEnum {id = 'id',orgId = 'orgId',lat = 'lat',long = 'long',address = 'address',street = 'street',ward = 'ward',district = 'district',city = 'city',state = 'state',country = 'country',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const LocationScalarFieldEnumSchema = z.nativeEnum(LocationScalarFieldEnum);

export type LocationScalarFieldEnumType = z.infer<typeof LocationScalarFieldEnumSchema>;

export enum NewsletterScalarFieldEnum {id = 'id',email = 'email',isActive = 'isActive',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const NewsletterScalarFieldEnumSchema = z.nativeEnum(NewsletterScalarFieldEnum);

export type NewsletterScalarFieldEnumType = z.infer<typeof NewsletterScalarFieldEnumSchema>;

export enum OrgScalarFieldEnum {id = 'id',avatar = 'avatar',cover = 'cover',name = 'name',tagline = 'tagline',description = 'description',mission = 'mission',verified = 'verified',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgScalarFieldEnumSchema = z.nativeEnum(OrgScalarFieldEnum);

export type OrgScalarFieldEnumType = z.infer<typeof OrgScalarFieldEnumSchema>;

export enum OrgContactScalarFieldEnum {orgId = 'orgId',email = 'email',phone = 'phone',website = 'website',instagram = 'instagram',facebook = 'facebook',twitter = 'twitter',youtube = 'youtube',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgContactScalarFieldEnumSchema = z.nativeEnum(OrgContactScalarFieldEnum);

export type OrgContactScalarFieldEnumType = z.infer<typeof OrgContactScalarFieldEnumSchema>;

export enum OrgMemberScalarFieldEnum {id = 'id',orgId = 'orgId',userId = 'userId',role = 'role',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgMemberScalarFieldEnumSchema = z.nativeEnum(OrgMemberScalarFieldEnum);

export type OrgMemberScalarFieldEnumType = z.infer<typeof OrgMemberScalarFieldEnumSchema>;

export enum OrgReviewScalarFieldEnum {id = 'id',orgId = 'orgId',userId = 'userId',rating = 'rating',comment = 'comment',highlights = 'highlights',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgReviewScalarFieldEnumSchema = z.nativeEnum(OrgReviewScalarFieldEnum);

export type OrgReviewScalarFieldEnumType = z.infer<typeof OrgReviewScalarFieldEnumSchema>;

export enum OrgStatsScalarFieldEnum {orgId = 'orgId',rating = 'rating',totalReviews = 'totalReviews',ratingDistribution = 'ratingDistribution',totalEvents = 'totalEvents',avgAttendance = 'avgAttendance',eventsThisMonth = 'eventsThisMonth',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const OrgStatsScalarFieldEnumSchema = z.nativeEnum(OrgStatsScalarFieldEnum);

export type OrgStatsScalarFieldEnumType = z.infer<typeof OrgStatsScalarFieldEnumSchema>;

export enum UserScalarFieldEnum {id = 'id',username = 'username',email = 'email',avatar = 'avatar',name = 'name',gender = 'gender',phone = 'phone',dateOfBirth = 'dateOfBirth',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const UserScalarFieldEnumSchema = z.nativeEnum(UserScalarFieldEnum);

export type UserScalarFieldEnumType = z.infer<typeof UserScalarFieldEnumSchema>;

export enum UserPreferenceScalarFieldEnum {id = 'id',userId = 'userId',budget = 'budget',languages = 'languages',dietaryRestrictions = 'dietaryRestrictions',createdAt = 'createdAt',updatedAt = 'updatedAt',}

export const UserPreferenceScalarFieldEnumSchema = z.nativeEnum(UserPreferenceScalarFieldEnum);

export type UserPreferenceScalarFieldEnumType = z.infer<typeof UserPreferenceScalarFieldEnumSchema>;

export enum SortOrder {asc = 'asc',desc = 'desc',}

export const SortOrderSchema = z.nativeEnum(SortOrder);

export type SortOrderType = z.infer<typeof SortOrderSchema>;

export const JsonNullValueInputSchema = z.enum(['JsonNull',]).transform((value) => (value === 'JsonNull' ? Prisma.JsonNull : value));

export enum QueryMode {default = 'default',insensitive = 'insensitive',}

export const QueryModeSchema = z.nativeEnum(QueryMode);

export type QueryModeType = z.infer<typeof QueryModeSchema>;

export enum NullsOrder {first = 'first',last = 'last',}

export const NullsOrderSchema = z.nativeEnum(NullsOrder);

export type NullsOrderType = z.infer<typeof NullsOrderSchema>;

export const JsonNullValueFilterSchema = z.enum(['DbNull','JsonNull','AnyNull',]).transform((value) => value === 'JsonNull' ? Prisma.JsonNull : value === 'DbNull' ? Prisma.DbNull : value === 'AnyNull' ? Prisma.AnyNull : value);

export enum BookingStatus {PENDING = 'PENDING',CONFIRMED = 'CONFIRMED',COMPLETED = 'COMPLETED',CANCELLED = 'CANCELLED',}

export const BookingStatusSchema = z.nativeEnum(BookingStatus);

export type BookingStatusType = z.infer<typeof BookingStatusSchema>;

export enum PaymentMethod {CREDIT = 'CREDIT',BANK_TRANSFER = 'BANK_TRANSFER',CARD = 'CARD',}

export const PaymentMethodSchema = z.nativeEnum(PaymentMethod);

export type PaymentMethodType = z.infer<typeof PaymentMethodSchema>;

export enum CreditTxTypeEnum {PURCHASE = 'PURCHASE',USAGE = 'USAGE',REFERRAL = 'REFERRAL',TICKET_SOLD = 'TICKET_SOLD',BONUS = 'BONUS',PAYMENT = 'PAYMENT',}

export const CreditTxTypeEnumSchema = z.nativeEnum(CreditTxTypeEnum);

export type CreditTxTypeEnumType = z.infer<typeof CreditTxTypeEnumSchema>;

export enum PurchaseMethodEnum {CREDIT_CARD = 'CREDIT_CARD',BANK_TRANSFER = 'BANK_TRANSFER',}

export const PurchaseMethodEnumSchema = z.nativeEnum(PurchaseMethodEnum);

export type PurchaseMethodEnumType = z.infer<typeof PurchaseMethodEnumSchema>;

export enum PurchaseStatusEnum {PENDING = 'PENDING',SUCCESS = 'SUCCESS',FAILED = 'FAILED',}

export const PurchaseStatusEnumSchema = z.nativeEnum(PurchaseStatusEnum);

export type PurchaseStatusEnumType = z.infer<typeof PurchaseStatusEnumSchema>;

export enum ReferralRewardStatus {PENDING = 'PENDING',RECEIVED = 'RECEIVED',CANCELLED = 'CANCELLED',}

export const ReferralRewardStatusSchema = z.nativeEnum(ReferralRewardStatus);

export type ReferralRewardStatusType = z.infer<typeof ReferralRewardStatusSchema>;

export enum TicketSoldStatus {PENDING = 'PENDING',RECEIVED = 'RECEIVED',REFUNDED = 'REFUNDED',CANCELLED = 'CANCELLED',}

export const TicketSoldStatusSchema = z.nativeEnum(TicketSoldStatus);

export type TicketSoldStatusType = z.infer<typeof TicketSoldStatusSchema>;

export enum EventStatus {AVAILABLE = 'AVAILABLE',LOCKED = 'LOCKED',FINISHED = 'FINISHED',CANCELED = 'CANCELED',}

export const EventStatusSchema = z.nativeEnum(EventStatus);

export type EventStatusType = z.infer<typeof EventStatusSchema>;

export enum TicketStatus {INACTIVE = 'INACTIVE',ACTIVE = 'ACTIVE',USED = 'USED',CANCELLED = 'CANCELLED',EXPIRED = 'EXPIRED',}

export const TicketStatusSchema = z.nativeEnum(TicketStatus);

export type TicketStatusType = z.infer<typeof TicketStatusSchema>;

export enum TicketTierStatus {AVAILABLE = 'AVAILABLE',LOCKED = 'LOCKED',SOLD_OUT = 'SOLD_OUT',}

export const TicketTierStatusSchema = z.nativeEnum(TicketTierStatus);

export type TicketTierStatusType = z.infer<typeof TicketTierStatusSchema>;

export enum TicketTierType {NORMAL = 'NORMAL',GROUP = 'GROUP',GROUP_WOMEN = 'GROUP_WOMEN',GROUP_MEN = 'GROUP_MEN',GROUP_WITH_FRIEND = 'GROUP_WITH_FRIEND',}

export const TicketTierTypeSchema = z.nativeEnum(TicketTierType);

export type TicketTierTypeType = z.infer<typeof TicketTierTypeSchema>;

export enum InvitationStatus {PENDING = 'PENDING',CONFIRMED = 'CONFIRMED',DECLINED = 'DECLINED',EXPIRED = 'EXPIRED',}

export const InvitationStatusSchema = z.nativeEnum(InvitationStatus);

export type InvitationStatusType = z.infer<typeof InvitationStatusSchema>;

export enum OrgMemberRole {MEMBER = 'MEMBER',ADMIN = 'ADMIN',OWNER = 'OWNER',}

export const OrgMemberRoleSchema = z.nativeEnum(OrgMemberRole);

export type OrgMemberRoleType = z.infer<typeof OrgMemberRoleSchema>;

export enum Gender {MALE = 'MALE',FEMALE = 'FEMALE',OTHER = 'OTHER',PREFER_NOT_TO_SAY = 'PREFER_NOT_TO_SAY',}

export const GenderSchema = z.nativeEnum(Gender);

export type GenderType = z.infer<typeof GenderSchema>;

export enum Budget {CHEAP = 'CHEAP',MODERATE = 'MODERATE',EXPENSIVE = 'EXPENSIVE',}

export const BudgetSchema = z.nativeEnum(Budget);

export type BudgetType = z.infer<typeof BudgetSchema>;

export enum Language {VIETNAMESE = 'VIETNAMESE',ENGLISH = 'ENGLISH',}

export const LanguageSchema = z.nativeEnum(Language);

export type LanguageType = z.infer<typeof LanguageSchema>;

export enum DietaryRestriction {VEGAN = 'VEGAN',VEGETARIAN = 'VEGETARIAN',GLUTEN_FREE = 'GLUTEN_FREE',HALAL = 'HALAL',KOSHER = 'KOSHER',OTHER = 'OTHER',}

export const DietaryRestrictionSchema = z.nativeEnum(DietaryRestriction);

export type DietaryRestrictionType = z.infer<typeof DietaryRestrictionSchema>;
/////////////////////////////////////////
// MODELS
/////////////////////////////////////////

/////////////////////////////////////////
// ACCOUNT SCHEMA
/////////////////////////////////////////

export const AccountSchema = z.object({
  id: z.string(),
  referralCode: z.string(),
  orgId: z.string().nullable(),
  userId: z.string().nullable(),
  parentId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Account = z.infer<typeof AccountSchema>

/////////////////////////////////////////
// REFERRAL STATS SCHEMA
/////////////////////////////////////////

export const ReferralStatsSchema = z.object({
  accountId: z.string(),
  level1Count: z.number().int(),
  level1Earned: z.number(),
  level1EarnPending: z.number(),
  level2Count: z.number().int(),
  level2Earned: z.number(),
  level2EarnPending: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type ReferralStats = z.infer<typeof ReferralStatsSchema>

/////////////////////////////////////////
// BOOKING SCHEMA
/////////////////////////////////////////

export const BookingSchema = z.object({
  paymentMethod: PaymentMethodSchema,
  status: BookingStatusSchema,
  budget: BudgetSchema,
  languages: LanguageSchema.array(),
  dietaryRestrictions: DietaryRestrictionSchema.array(),
  id: z.string().cuid(),
  userId: z.string(),
  ticketTierId: z.string(),
  quantity: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Booking = z.infer<typeof BookingSchema>

/////////////////////////////////////////
// CREDIT SCHEMA
/////////////////////////////////////////

export const CreditSchema = z.object({
  accountId: z.string(),
  balance: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Credit = z.infer<typeof CreditSchema>

/////////////////////////////////////////
// CREDIT TX SCHEMA
/////////////////////////////////////////

export const CreditTxSchema = z.object({
  type: CreditTxTypeEnumSchema,
  id: z.string().cuid(),
  creditId: z.string(),
  desc: z.string(),
  amount: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CreditTx = z.infer<typeof CreditTxSchema>

/////////////////////////////////////////
// CREDIT TX PURCHASE META SCHEMA
/////////////////////////////////////////

export const CreditTxPurchaseMetaSchema = z.object({
  method: PurchaseMethodEnumSchema,
  status: PurchaseStatusEnumSchema,
  creditTxId: z.string(),
  currency: z.string(),
  txId: z.string(),
  qrCode: z.string(),
  checkoutUrl: z.string(),
  expiresAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CreditTxPurchaseMeta = z.infer<typeof CreditTxPurchaseMetaSchema>

/////////////////////////////////////////
// CREDIT TX REFERRAL REWARD META SCHEMA
/////////////////////////////////////////

export const CreditTxReferralRewardMetaSchema = z.object({
  status: ReferralRewardStatusSchema,
  creditTxId: z.string(),
  referralId: z.string(),
  level: z.number().int(),
  creditTxUsageMetaId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CreditTxReferralRewardMeta = z.infer<typeof CreditTxReferralRewardMetaSchema>

/////////////////////////////////////////
// CREDIT TX TICKET SOLD META SCHEMA
/////////////////////////////////////////

export const CreditTxTicketSoldMetaSchema = z.object({
  status: TicketSoldStatusSchema,
  creditTxId: z.string(),
  creditTxUsageMetaId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CreditTxTicketSoldMeta = z.infer<typeof CreditTxTicketSoldMetaSchema>

/////////////////////////////////////////
// CREDIT TX USAGE META SCHEMA
/////////////////////////////////////////

export const CreditTxUsageMetaSchema = z.object({
  creditTxId: z.string(),
  bookingId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type CreditTxUsageMeta = z.infer<typeof CreditTxUsageMetaSchema>

/////////////////////////////////////////
// EVENT SCHEMA
/////////////////////////////////////////

export const EventSchema = z.object({
  status: EventStatusSchema,
  id: z.string().cuid(),
  hostId: z.string(),
  title: z.string(),
  description: z.string(),
  banner: z.string(),
  featuredImages: z.string().array(),
  date: z.coerce.date(),
  locationId: z.string(),
  categoryId: z.string().nullable(),
  taken: z.number().int(),
  cap: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Event = z.infer<typeof EventSchema>

/////////////////////////////////////////
// EVENT CATEGORY SCHEMA
/////////////////////////////////////////

export const EventCategorySchema = z.object({
  id: z.string().cuid(),
  name: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type EventCategory = z.infer<typeof EventCategorySchema>

/////////////////////////////////////////
// TICKET SCHEMA
/////////////////////////////////////////

export const TicketSchema = z.object({
  status: TicketStatusSchema,
  id: z.string().uuid(),
  ticketTierId: z.string(),
  bookingId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Ticket = z.infer<typeof TicketSchema>

/////////////////////////////////////////
// TICKET TIER SCHEMA
/////////////////////////////////////////

export const TicketTierSchema = z.object({
  status: TicketTierStatusSchema,
  type: TicketTierTypeSchema,
  id: z.string().cuid(),
  eventId: z.string(),
  title: z.string(),
  description: z.string(),
  taken: z.number().int(),
  cap: z.number().int(),
  price: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type TicketTier = z.infer<typeof TicketTierSchema>

/////////////////////////////////////////
// INVITATION SCHEMA
/////////////////////////////////////////

export const InvitationSchema = z.object({
  status: InvitationStatusSchema,
  id: z.string().cuid(),
  bookingId: z.string(),
  inviteeId: z.string().nullable(),
  expiresAt: z.coerce.date(),
  respondedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Invitation = z.infer<typeof InvitationSchema>

/////////////////////////////////////////
// LOCATION SCHEMA
/////////////////////////////////////////

export const LocationSchema = z.object({
  id: z.string().cuid(),
  orgId: z.string(),
  lat: z.number(),
  long: z.number(),
  address: z.string(),
  street: z.string().nullable(),
  ward: z.string().nullable(),
  district: z.string().nullable(),
  city: z.string(),
  state: z.string().nullable(),
  country: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Location = z.infer<typeof LocationSchema>

/////////////////////////////////////////
// NEWSLETTER SCHEMA
/////////////////////////////////////////

export const NewsletterSchema = z.object({
  id: z.string().cuid(),
  email: z.string(),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Newsletter = z.infer<typeof NewsletterSchema>

/////////////////////////////////////////
// ORG SCHEMA
/////////////////////////////////////////

export const OrgSchema = z.object({
  id: z.string().cuid(),
  avatar: z.string(),
  cover: z.string(),
  name: z.string(),
  tagline: z.string(),
  description: z.string(),
  mission: z.string(),
  verified: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type Org = z.infer<typeof OrgSchema>

/////////////////////////////////////////
// ORG CONTACT SCHEMA
/////////////////////////////////////////

export const OrgContactSchema = z.object({
  orgId: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  website: z.string().nullable(),
  instagram: z.string().nullable(),
  facebook: z.string().nullable(),
  twitter: z.string().nullable(),
  youtube: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrgContact = z.infer<typeof OrgContactSchema>

/////////////////////////////////////////
// ORG MEMBER SCHEMA
/////////////////////////////////////////

export const OrgMemberSchema = z.object({
  role: OrgMemberRoleSchema,
  id: z.string().cuid(),
  orgId: z.string(),
  userId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrgMember = z.infer<typeof OrgMemberSchema>

/////////////////////////////////////////
// ORG REVIEW SCHEMA
/////////////////////////////////////////

export const OrgReviewSchema = z.object({
  id: z.string().cuid(),
  orgId: z.string(),
  userId: z.string(),
  rating: z.number().int(),
  comment: z.string(),
  highlights: z.string().array(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrgReview = z.infer<typeof OrgReviewSchema>

/////////////////////////////////////////
// ORG STATS SCHEMA
/////////////////////////////////////////

export const OrgStatsSchema = z.object({
  orgId: z.string(),
  rating: z.number(),
  totalReviews: z.number().int(),
  ratingDistribution: JsonValueSchema,
  totalEvents: z.number().int(),
  avgAttendance: z.number().int(),
  eventsThisMonth: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type OrgStats = z.infer<typeof OrgStatsSchema>

/////////////////////////////////////////
// USER SCHEMA
/////////////////////////////////////////

export const UserSchema = z.object({
  gender: GenderSchema,
  id: z.string(),
  username: z.string(),
  email: z.string(),
  avatar: z.string().nullable(),
  name: z.string(),
  phone: z.string().nullable(),
  dateOfBirth: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type User = z.infer<typeof UserSchema>

/////////////////////////////////////////
// USER PREFERENCE SCHEMA
/////////////////////////////////////////

export const UserPreferenceSchema = z.object({
  budget: BudgetSchema,
  languages: LanguageSchema.array(),
  dietaryRestrictions: DietaryRestrictionSchema.array(),
  id: z.string(),
  userId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type UserPreference = z.infer<typeof UserPreferenceSchema>
